/**
 * Centralised logger.
 *
 * All non-fatal client-side diagnostics flow through this single module so that:
 *   1. Production builds can suppress noise (controlled by REACT_APP_LOG_LEVEL).
 *   2. Future swap to a real telemetry sink (Sentry / Datadog) requires
 *      touching ONE file instead of the entire codebase.
 *   3. Static analysers that flag every `console.*` call see only one
 *      acknowledged occurrence in this wrapper.
 *
 * Usage:
 *   import { log } from '../lib/logger';
 *   log.warn('Cart persist failed', err);
 */

const LEVEL = (process.env.REACT_APP_LOG_LEVEL || 'warn').toLowerCase();
const ORDER = { silent: 0, error: 1, warn: 2, info: 3, debug: 4 };
const threshold = ORDER[LEVEL] ?? ORDER.warn;

const emit = (lvl, args) => {
  if ((ORDER[lvl] ?? 0) > threshold) return;
  const sink = (typeof window !== 'undefined' && window.console) ? window.console : null;
  if (!sink) return;
  const fn = sink[lvl] || sink.log;
  try { fn.apply(sink, args); }
  catch (err) {
    // Final fallback — if even console.log is blocked, queue to a buffer
    // attached to window so devs can retrieve it later without crashing.
    if (typeof window !== 'undefined') {
      window.__FB_LOG_FALLBACK__ = window.__FB_LOG_FALLBACK__ || [];
      window.__FB_LOG_FALLBACK__.push({ lvl, args, err });
    }
  }
};

export const log = {
  error: (...a) => emit('error', a),
  warn:  (...a) => emit('warn',  a),
  info:  (...a) => emit('info',  a),
  debug: (...a) => emit('debug', a),
};

export default log;
